import { PathfindingGraph } from "./PathfindingGraph";
import { IPathfindingGoalCondition } from "./IPathfindingGoalCondition";
import { IPathfindingHeuristic } from "./IPathfindingHeuristic";
import { IntPoint } from "./IntPoint";

import { IndexedPriorityQueue } from "./IndexedPriorityQueue";

/**
 * The Pathfinder provides functionality to search a graph for a path given certain goal conditions and a heuristic.
 * @author npuschke
 *
 */
export class Pathfinder {
    private static STATE_UNSOLVED: number = 0;
    private static STATE_SOLUTION_FOUND: number = 1;
    private static STATE_NO_SOLUTION_FOUND: number = 2;

    private static NODE_NEW: number = 0;
    private static NODE_OPEN: number = 1;
    private static NODE_CLOSED: number = 2;

    private _graph: PathfindingGraph;
    private _nodeStart: number = 0;
    private _nodeGoal: number = 0;
    private _goalcondition: IPathfindingGoalCondition;
    private _heuristic: IPathfindingHeuristic;

    private _state: number = Pathfinder.STATE_UNSOLVED;

    private _node_parent: number[];

    /**
     * Creates a pathfinder object that can be used to search a <strong>graph</strong> for a path. The search will start at a given <strong>start node</strong> and determines the shortest path to the nearest node that satisfies the <strong>goal conditions</strong>. Its search direction can be influenced by a <strong>heuristic</strong>.
     * <p>To actually run the search, use <code>solve()</code>.</p>
     *
     * <p>Different goal conditions can e.g. be used to not only search for the path to a certain target node, but also to a target area or the nearest food source etc.
     * Custom goal conditions can be implemented using the <code>IPathfindingGoalCondition</code> interface.</p>
     *
     * <p>Different heuristics can be used to nudge the search into the right direction to improve its runtime performance.
     * Custom heuristics can be implemented using the <code>IPathfindingHeuristic</code> interface.</p>
     *
     * @param graph The graph of connected nodes that will be traversed by the search.
     * @param startNodeIndex The index of the start node in the graph.
     * @param goalcondition The goal conditions that each traversed node gets checked against to determine whether its a valid target node.
     * @param heuristic The mathematical approximation that nudges the search algorithm to the correct direction to improve its performance.
     *
     * @see com.goodgamestudios.pathfinding.PathfindingGraph
     * @see com.goodgamestudios.pathfinding.IPathfindingGoalCondition
     * @see com.goodgamestudios.pathfinding.PathfindingGoalConditionTargetPoint
     * @see com.goodgamestudios.pathfinding.PathfindingGoalConditionTargetRect
     * @see com.goodgamestudios.pathfinding.PathfindingGoalConditionTargetRectAdjacent
     * @see com.goodgamestudios.pathfinding.IPathfindingHeuristic
     * @see com.goodgamestudios.pathfinding.PathfindingHeuristicEuclidian
     * @see com.goodgamestudios.pathfinding.PathfindingHeuristicManhattan
     * @see com.goodgamestudios.pathfinding.PathfindingHeuristicNone
     */
    constructor(graph: PathfindingGraph, startNodeIndex: number, goalcondition: IPathfindingGoalCondition, heuristic: IPathfindingHeuristic) {
        this._graph = graph;
        this._nodeStart = startNodeIndex;
        this._goalcondition = goalcondition;
        this._heuristic = heuristic;
    }

    /**
     * <p>Searches the graph for the shortest path from the start node to the nearest target node - using the given heuristic and goal condition.</p>
     * <p>Use getPath() to retrieve found path afterwards.</p>
     * @return <code>true</code> if a path has been found, <code>false</code> if not.
     */
    public solve(): boolean {
        let solved: boolean = false;

        // perform quick check if goal can be reached at all
        if (this._nodeStart >= 0 && this._nodeStart < this._graph.nodeCount && !this._goalcondition.isUnreachable(this._graph)) {
            // search status of each node - 0: not encountered; 1: being looked into (open); 2: completely analysed (closed)
            let node_status: number[] = Array(this._graph.nodeCount).fill(0);
            // parent node of each node - keeps track how we came to a node
            this._node_parent = Array(this._graph.nodeCount).fill(0);
            // costs for each node
            let node_gcost: number[] = Array(this._graph.nodeCount).fill(0);
            let node_hcost: number[] = Array(this._graph.nodeCount).fill(0);
            let node_fcost: number[] = Array(this._graph.nodeCount).fill(0);

            // create list of open nodes - nodes that are on the 'frontier' of the search
            let openList: IndexedPriorityQueue = new IndexedPriorityQueue(node_fcost);

            // begin the search at the start node
            let currentNode: number = this._nodeStart;

            node_status[currentNode] = Pathfinder.NODE_OPEN;
            openList.sortInto(currentNode);

            // let the search begin!
            while (!solved && (openList.length() > 0)) {
                // grab the first node - its the one with the lowest f cost
                currentNode = openList.pop();

                // is this node a solution?
                if (this._goalcondition.isSatisfied(this._graph.getNodeX(currentNode), this._graph.getNodeY(currentNode))) {
                    this._nodeGoal = currentNode;
                    solved = true;
                }
                else {
                    // expand search to its neighbors
                    let neighborNodes: number[] = this._graph.getNodeNeighbors(currentNode);
                    let neighborCosts: number[] = this._graph.getConnectionCosts(currentNode);
                    let neighborNode: number = 0;
                    let newCostG: number = 0;

                    for (let neighborNodeIndex: number = 0; neighborNodeIndex < neighborNodes.length; neighborNodeIndex++) {
                        neighborNode = neighborNodes[neighborNodeIndex];

                        // only investigate into neighbor nodes not analyzed yet
                        if (node_status[neighborNode] != Pathfinder.NODE_CLOSED) {
                            if (neighborCosts != null) {
                                newCostG = node_gcost[currentNode] + neighborCosts[neighborNodeIndex];
                            }
                            else {
                                newCostG = node_gcost[currentNode] + this._graph.traverseCostStandard;
                            }

                            // is this a node we haven't seen before?
                            if (node_status[neighborNode] == Pathfinder.NODE_NEW) {
                                // calc its travel costs
                                node_gcost[neighborNode] = newCostG;
                                node_hcost[neighborNode] = this._heuristic.calculate(this._graph, this._graph.getNodeX(neighborNode), this._graph.getNodeY(neighborNode));
                                node_fcost[neighborNode] = node_gcost[neighborNode] + node_hcost[neighborNode];

                                // save our path to it
                                this._node_parent[neighborNode] = currentNode;

                                // add it to the search frontier
                                openList.sortInto(neighborNode);
                                node_status[neighborNode] = Pathfinder.NODE_OPEN;
                            }
                            else // we have met this node before!
                            {
                                // has the travel cost been higher before?
                                if (newCostG < node_gcost[neighborNode]) {
                                    // overwrite old costs and path - the new path is better!
                                    node_gcost[neighborNode] = newCostG;
                                    node_fcost[neighborNode] = newCostG + node_hcost[neighborNode];
                                    this._node_parent[neighborNode] = currentNode;
                                }
                            }
                        }
                    }
                    // we're done analyzing the current node and won't look at it ever again
                    node_status[currentNode] = Pathfinder.NODE_CLOSED;
                }
            }
        }

        if (solved) {
            //		trace("pathfinding done in " + (getTimer() - timer) + "ms");
            this._state = Pathfinder.STATE_SOLUTION_FOUND;
        }
        else {
            //		trace("pathfinding aborted after " + (getTimer() - timer) + "ms");
            this._state = Pathfinder.STATE_NO_SOLUTION_FOUND;
        }

        return solved;
    }

    /**
     * <p>Retrieves the found path. It consists of IntPoints representing the path node's coordinates.</p>
     * <p>Please note that the path is stored in reversed order: The first element is the target node, the last element the start node.</p>
     * @return The path that has been found by <code>solve()</code> - <code>null</code> if none found/search not run.
     *
     */
    public getPath(): IntPoint[] {
        let path: IntPoint[] = null;

        if (this._state == Pathfinder.STATE_SOLUTION_FOUND) {
            path = [];

            // start at the end
            let currentNode: number = this._nodeGoal;

            // trace back from node to node
            while (currentNode != this._nodeStart) {
                path.push(new IntPoint(this._graph.getNodeX(currentNode), this._graph.getNodeY(currentNode)));
                currentNode = this._node_parent[currentNode];
            }

            // finally, add the start position
            path.push(new IntPoint(this._graph.getNodeX(currentNode), this._graph.getNodeY(currentNode)));
        }
        return path;
    }

    /**
     * Determines whether the search has found a path.
     * @return <code>true</code> if search completed successfully, <code>false</code> if no path found/not run.
     *
     */
    public get solved(): boolean {
        return this._state == Pathfinder.STATE_SOLUTION_FOUND;
    }
}


